import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
export type AviationDashboardFilterOptions = {
  lesseeRegions: string[];
  dealNames: string[];
  aircraftTypes: string[];
};

const FILTER_OPTIONS_PATH = 'aviation-dashboard/filter-options';
const AS_OF_YEARS_PATH = 'aviation-dashboard/as-of-years';
const AS_OF_DATES_PATH = 'aviation-dashboard/as-of-dates';

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

/** BaseService.get returns parsed JSON, or a raw Response if content-type is not JSON. */

function intArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): number[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: number[] = [];
  for (const x of raw) {
    if (typeof x === 'number' && Number.isFinite(x)) {
      out.push(Math.trunc(x));
    } else if (typeof x === 'string' && /^\d+$/.test(x)) {
      out.push(Number.parseInt(x, 10));
    }
  }
  return out;
}

/** Distinct calendar years from <c>AS_OF_DATE</c> on SP_AVIATION_LOAN_TAPE (newest first). */

export type AviationPitSlice = {
  category: string;
  value: number;
};

export type AviationPitDimension = 'aircraft-type' | 'lessee-region' | 'servicer' | 'facility-sponsor';

const PIT_SLICE_PATH: Record<AviationPitDimension, string> = {
  'aircraft-type': 'aviation-dashboard/point-in-time/aircraft-type-slices',
  'lessee-region': 'aviation-dashboard/point-in-time/lessee-region-slices',
  servicer: 'aviation-dashboard/point-in-time/servicer-slices',
  'facility-sponsor': 'aviation-dashboard/point-in-time/facility-sponsor-slices',
};

function pitSlicesFromBody(body: Record<string, unknown>): AviationPitSlice[] {
  const raw = body.slices ?? body.Slices;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: AviationPitSlice[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') {
      continue;
    }
    const r = row as Record<string, unknown>;
    const rawCat = r.category ?? r.Category ?? r.aircraftType ?? r.AircraftType;
    const v = r.value ?? r.Value;
    if (typeof rawCat !== 'string' || rawCat.length === 0 || v == null) {
      continue;
    }
    const cat = rawCat;
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) {
      continue;
    }
    out.push({ category: cat, value: n });
  }
  return out;
}

/** Lessee-country bar: Top N + Others, or every country. Sent as <c>lesseeCountryLimit</c> on the API. */
export type LesseeCountryBarLimit = '10' | '20' | 'all';

export type AviationTapeDashboardKpiLatest = {
  asOfDateIso: string;
  totalOutstandingAmount: number;
  aircraftMsns: number;
  weightedAvgLtvPct: number | null;
  waRemainingLeaseMonths: number | null;
};

function parseNum(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseKpiRow(raw: unknown): AviationTapeDashboardKpiLatest | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDateIso ?? o.AsOfDateIso;
  if (typeof asOf !== 'string' || asOf.length === 0) {
    return null;
  }
  const totalOutstandingAmount = parseNum(o.totalOutstandingAmount ?? o.TotalOutstandingAmount);
  const aircraftMsns = parseNum(o.aircraftMsns ?? o.AircraftMsns);
  if (totalOutstandingAmount == null || aircraftMsns == null) {
    return null;
  }
  const weightedAvgLtvPct = parseNum(o.weightedAvgLtvPct ?? o.WeightedAvgLtvPct);
  const waRemainingLeaseMonths = parseNum(o.waRemainingLeaseMonths ?? o.WaRemainingLeaseMonths);

  return {
    asOfDateIso: asOf,
    totalOutstandingAmount,
    aircraftMsns,
    weightedAvgLtvPct,
    waRemainingLeaseMonths,
  };
}

/**
 * Snapshot KPIs for SP_AVIATION_LOAN_TAPE, optionally filtered by region, deal, aircraft type, and as-of date.
 */

export type AviationDashboardFilters = {
  lesseeRegion: readonly string[];
  dealName: readonly string[];
  aircraftType: readonly string[];
  /** Selected tape AS_OF_DATE (YYYY-MM-DD). Empty = latest. */
  asOfDate?: string;
};

function appendAviationDashboardFilterParams(qs: URLSearchParams, filters: AviationDashboardFilters) {
  for (const v of filters.lesseeRegion) {
    const t = v.trim();
    if (t) qs.append('lesseeRegion', t);
  }
  for (const v of filters.dealName) {
    const t = v.trim();
    if (t) qs.append('dealName', t);
  }
  for (const v of filters.aircraftType) {
    const t = v.trim();
    if (t) qs.append('aircraftType', t);
  }
  const asOf = filters.asOfDate?.trim();
  if (asOf) qs.set('asOfDate', asOf);
}

export type AviationLeaseExpirationForecastRow = {
  forecastMonthIso: string;
  activeAircraftCount: number;
  endedAircraftCount: number;
  expiringInMonthCount: number;
};

function parseLeaseExpirationRows(raw: unknown): AviationLeaseExpirationForecastRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: AviationLeaseExpirationForecastRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const iso = r.forecastMonthIso ?? r.ForecastMonthIso;
    if (typeof iso !== 'string' || iso.length === 0) {
      continue;
    }
    const active = parseNum(r.activeAircraftCount ?? r.ActiveAircraftCount);
    const ended = parseNum(r.endedAircraftCount ?? r.EndedAircraftCount);
    const expiring = parseNum(r.expiringInMonthCount ?? r.ExpiringInMonthCount);
    if (active == null || ended == null || expiring == null) {
      continue;
    }
    out.push({
      forecastMonthIso: iso,
      activeAircraftCount: Math.trunc(active),
      endedAircraftCount: Math.trunc(ended),
      expiringInMonthCount: Math.trunc(expiring),
    });
  }
  return out;
}

/**
 * Lease expiration combo chart series (12 months forward from tape anchor), same dashboard filters as KPI / tape charts.
 */

export class AviationDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return {};
  };

  fetchAviationDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      lesseeRegions: stringArrayFromBody(body, 'lesseeRegions', 'LesseeRegions'),
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
      aircraftTypes: stringArrayFromBody(body, 'aircraftTypes', 'AircraftTypes'),
    };
  };

  fetchAviationAsOfYears = async () => {
    const body = await this.getJsonBody(AS_OF_YEARS_PATH);
    return intArrayFromBody(body, 'years', 'Years');
  };

  fetchAviationDashboardAsOfDates = async () => {
    const body = await this.getJsonBody(AS_OF_DATES_PATH);
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchAviationPointInTimeSlices = async (
    dimension: AviationPitDimension,
    params: { year: number; measure: string; dealNames: readonly string[] },
  ) => {
    const qs = new URLSearchParams();
    qs.set('year', String(params.year));
    qs.set('measure', params.measure);
    for (const d of params.dealNames) {
      const t = d.trim();
      if (t) {
        qs.append('dealName', t);
      }
    }
    const path = `${PIT_SLICE_PATH[dimension]}?${qs.toString()}`;
    const body = await this.getJsonBody(path);
    return pitSlicesFromBody(body);
  };

  fetchAviationPointInTimeLesseeCountrySlices = async (params: {
    year: number;
    measure: string;
    dealNames: readonly string[];
    limit: LesseeCountryBarLimit;
  }) => {
    const qs = new URLSearchParams();
    qs.set('year', String(params.year));
    qs.set('measure', params.measure);
    qs.set('lesseeCountryLimit', params.limit === 'all' ? 'all' : params.limit);
    for (const d of params.dealNames) {
      const t = d.trim();
      if (t) {
        qs.append('dealName', t);
      }
    }
    const path = `aviation-dashboard/point-in-time/lessee-country-slices?${qs.toString()}`;
    const body = await this.getJsonBody(path);
    return pitSlicesFromBody(body);
  };

  fetchAviationTapeDashboardKpi = async (filters: AviationDashboardFilters) => {
    const qs = new URLSearchParams();
    appendAviationDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `aviation-dashboard/tape-kpi?${q}` : 'aviation-dashboard/tape-kpi';

    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return null;
      }
      const parsed: unknown = JSON.parse(text) as unknown;
      return parseKpiRow(parsed);
    }
    return parseKpiRow(raw);
  };

  fetchAviationLeaseExpirationForecast = async (filters: AviationDashboardFilters) => {
    const qs = new URLSearchParams();
    appendAviationDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0
        ? `aviation-dashboard/lease-expiration-forecast?${q}`
        : 'aviation-dashboard/lease-expiration-forecast';

    const body = await this.getJsonBody(path);
    return parseLeaseExpirationRows(body);
  };
}
